import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import type { LabeledCoordinates } from "@/types/location";
import type { LocationResult } from "@mm/geocoder.meteomatics.com/models";
import { useCallback, useState } from "react";

export type LocationSelectOptions = { isCoordInput?: boolean };

/** Converts a search result to coordinates. A label is only kept if the user didn't type raw coordinates. */
export function toLabeledCoordinates(location: LocationResult, options?: LocationSelectOptions): LabeledCoordinates {
  return {
    lat: location.geometry.lat,
    lon: location.geometry.lng,
    label: options?.isCoordInput ? undefined : location.formatted,
  };
}

type Params = {
  sourceCellName?: string;
  showStationSearch: boolean;
  onSelect: (location: LocationResult, options?: LocationSelectOptions) => void;
};

/** Opens the shared location search window for a label and tracks whether this label's window is open. */
export function useLocationSearchWindow({ sourceCellName, showStationSearch, onSelect }: Params) {
  const desktop = useDesktop();
  const windowId = instanceId(WindowKind.LocationSearchWindow);
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);

  const open = () => {
    setIsOpen(true);
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId,
      windowKind: WindowKind.LocationSearchWindow,
      customProps: {
        onSelect: (location, options) => {
          onSelect(location, options);
          setIsOpen(false);
        },
        onCloseOverlay: close,
        sourceCellName,
        showStationSearch,
        isAdvancedStationView: false,
      },
    });
    desktop.dispatch({ type: "setWindowVisibility", windowId, isVisible: true });
  };

  return { isOpen, open };
}
